import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Gift, Diamond, Star, Heart, Flame, Crown, Sparkles, Zap,
  TrendingUp, Award, X, Send, Coins
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Premium gift IDs that trigger full-screen effects
const PREMIUM_GIFT_IDS = ['castle', 'planet'];

// Available gifts with their values
const GIFTS = [
  { id: 'heart', name: 'Heart', icon: '❤️', diamonds: 1, color: '#FF2A2A', premium: false },
  { id: 'star', name: 'Star', icon: '⭐', diamonds: 5, color: '#FFE800', premium: false },
  { id: 'rose', name: 'Rose', icon: '🌹', diamonds: 10, color: '#FF69B4', premium: false },
  { id: 'crown', name: 'Crown', icon: '👑', diamonds: 50, color: '#FFD700', premium: false },
  { id: 'diamond', name: 'Diamond', icon: '💎', diamonds: 100, color: '#00F0FF', premium: false },
  { id: 'rocket', name: 'Rocket', icon: '🚀', diamonds: 200, color: '#FF6B6B', premium: false },
  { id: 'castle', name: 'Castle', icon: '🏰', diamonds: 500, color: '#A855F7', premium: true },
  { id: 'planet', name: 'Planet', icon: '🪐', diamonds: 1000, color: '#00FF66', premium: true },
];

// Floating gift animation component
const FloatingGift = ({ gift, onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, 2000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.5, y: 50 }}
      animate={{ 
        opacity: [0, 1, 1, 0],
        scale: [0.5, 1.2, 1, 0.8],
        y: [50, 0, -100, -200],
        x: [0, Math.random() * 100 - 50, Math.random() * 150 - 75]
      }}
      transition={{ duration: 2, ease: 'easeOut' }}
      className="absolute bottom-20 right-4 pointer-events-none z-50"
    >
      <div className="flex flex-col items-center">
        <span className="text-5xl">{gift.icon}</span>
        <div className="flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm">
          <Diamond className="w-3 h-3 text-[#00F0FF]" />
          <span className="text-xs font-bold text-white">+{gift.diamonds}</span>
        </div>
      </div>
    </motion.div>
  );
};

// Gift combo animation
const GiftCombo = ({ count, gift }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0 }}
    className="absolute bottom-32 right-4 pointer-events-none z-50"
  >
    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#FF2A2A] to-[#FFE800] shadow-lg">
      <span className="text-2xl">{gift.icon}</span>
      <span className="text-2xl font-black text-white">x{count}</span>
      <Zap className="w-5 h-5 text-white animate-pulse" />
    </div>
  </motion.div>
);

// Gift panel for sending gifts
export const GiftPanel = ({ isOpen, onClose, onSendGift, onPremiumGift, userDiamonds = 0, senderInfo = null, onGetDiamonds = () => {} }) => {
  const [selectedGift, setSelectedGift] = useState(null);
  const [quantity, setQuantity] = useState(1);

  const handleSend = () => {
    if (selectedGift && userDiamonds >= selectedGift.diamonds * quantity) {
      onSendGift(selectedGift, quantity);
      
      // Trigger premium gift effect if applicable
      if (selectedGift.premium && onPremiumGift) {
        onPremiumGift(selectedGift.id, senderInfo);
      }
      
      setSelectedGift(null);
      setQuantity(1);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-50 bg-[#0A0A0F]/95 backdrop-blur-2xl border-t border-white/10 rounded-t-3xl"
          data-testid="gift-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/5">
            <div className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-[#FFE800]" />
              <span className="font-semibold text-white">Send Gift</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onGetDiamonds}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#00F0FF]/20 to-[#00F0FF]/10 border border-[#00F0FF]/30 hover:border-[#00F0FF]/60 transition-all"
                data-testid="get-diamonds-btn"
              >
                <Diamond className="w-4 h-4 text-[#00F0FF]" />
                <span className="text-sm font-bold text-[#00F0FF]">{userDiamonds.toLocaleString()}</span>
                <span className="text-xs text-[#00F0FF]/70">+</span>
              </button>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 text-gray-400">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Gift Grid */}
          <div className="p-4 grid grid-cols-4 gap-3 max-h-[300px] overflow-y-auto">
            {GIFTS.map((gift) => (
              <motion.button
                key={gift.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedGift(gift)}
                className={`relative flex flex-col items-center gap-2 p-3 rounded-2xl transition-all ${
                  selectedGift?.id === gift.id
                    ? 'bg-white/10 ring-2 ring-[#00F0FF]'
                    : 'bg-white/5 hover:bg-white/10'
                } ${gift.premium ? 'border border-[#A855F7]/30' : ''}`}
                data-testid={`gift-${gift.id}`}
              >
                {/* Premium badge */}
                {gift.premium && (
                  <div className="absolute -top-1 -right-1 px-1.5 py-0.5 bg-gradient-to-r from-[#A855F7] to-[#FF69B4] rounded-full">
                    <span className="text-[8px] font-bold text-white">VIP</span>
                  </div>
                )}
                <span className="text-3xl">{gift.icon}</span>
                <span className="text-xs text-gray-400">{gift.name}</span>
                <div className="flex items-center gap-1">
                  <Diamond className="w-3 h-3 text-[#00F0FF]" />
                  <span className="text-xs font-bold text-white">{gift.diamonds}</span>
                </div>
              </motion.button>
            ))}
          </div>

          {/* Quantity & Send */}
          {selectedGift && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 border-t border-white/5"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{selectedGift.icon}</span>
                  <div>
                    <p className="font-semibold text-white">{selectedGift.name}</p>
                    <p className="text-sm text-gray-400">
                      <Diamond className="w-3 h-3 inline text-[#00F0FF]" /> {selectedGift.diamonds * quantity}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {/* Quantity selector */}
                  <div className="flex items-center gap-1 bg-white/5 rounded-full p-1">
                    {[1, 5, 10, 99].map((q) => (
                      <button
                        key={q}
                        onClick={() => setQuantity(q)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          quantity === q
                            ? 'bg-[#00F0FF] text-black'
                            : 'text-gray-400 hover:text-white'
                        }`}
                      >
                        {q === 99 ? '99+' : q}
                      </button>
                    ))}
                  </div>

                  {/* Send button */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleSend}
                    disabled={userDiamonds < selectedGift.diamonds * quantity}
                    className={`px-6 py-3 rounded-full font-semibold flex items-center gap-2 ${
                      userDiamonds >= selectedGift.diamonds * quantity
                        ? 'bg-gradient-to-r from-[#FF2A2A] to-[#FFE800] text-white'
                        : 'bg-gray-800 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <Send className="w-4 h-4" />
                    Send
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Live stats display for streamers
export const LiveStreamStats = ({ 
  diamonds = 0, 
  viewers = 0, 
  duration = '0:00',
  topGifters = [],
  recentGifts = [] 
}) => {
  return (
    <div className="absolute top-4 left-4 right-4 flex items-start justify-between z-30 pointer-events-none">
      {/* Left side stats */}
      <div className="flex flex-col gap-2 pointer-events-auto">
        {/* Viewer count */}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm">
          <div className="w-2 h-2 bg-[#FF2A2A] rounded-full animate-pulse" />
          <span className="text-sm font-medium text-white">{viewers.toLocaleString()}</span>
        </div>

        {/* Duration */}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm">
          <Flame className="w-4 h-4 text-[#FF6B6B]" />
          <span className="text-sm font-medium text-white">{duration}</span>
        </div>
      </div>

      {/* Right side - Diamond earnings */}
      <div className="flex flex-col items-end gap-2 pointer-events-auto">
        {/* Total diamonds */}
        <motion.div 
          key={diamonds}
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#00F0FF]/20 to-[#00FF66]/20 border border-[#00F0FF]/30 backdrop-blur-sm"
        >
          <Diamond className="w-5 h-5 text-[#00F0FF]" />
          <span className="text-lg font-bold text-white">{diamonds.toLocaleString()}</span>
        </motion.div>

        {/* Top gifters */}
        {topGifters.length > 0 && (
          <div className="flex items-center gap-1">
            {topGifters.slice(0, 3).map((gifter, index) => (
              <div
                key={gifter.id}
                className="relative"
                style={{ marginLeft: index > 0 ? '-8px' : 0, zIndex: 3 - index }}
              >
                <img
                  src={gifter.avatar || `https://ui-avatars.com/api/?name=${gifter.name}&background=0F0F16&color=00F0FF`}
                  alt={gifter.name}
                  className={`w-8 h-8 rounded-full border-2 ${
                    index === 0 ? 'border-[#FFD700]' : index === 1 ? 'border-[#C0C0C0]' : 'border-[#CD7F32]'
                  }`}
                />
                {index === 0 && (
                  <Crown className="absolute -top-2 -right-1 w-4 h-4 text-[#FFD700]" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Gift button for viewers
export const GiftButton = ({ onClick }) => (
  <motion.button
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
    onClick={onClick}
    className="relative p-4 rounded-full bg-gradient-to-br from-[#FF2A2A] to-[#FFE800] shadow-lg shadow-[#FF2A2A]/30"
    data-testid="gift-button"
  >
    <Gift className="w-6 h-6 text-white" />
    <motion.div
      animate={{ scale: [1, 1.3, 1] }}
      transition={{ duration: 2, repeat: Infinity }}
      className="absolute inset-0 rounded-full bg-gradient-to-br from-[#FF2A2A] to-[#FFE800] opacity-30"
    />
  </motion.button>
);

// Leaderboard component
export const GiftLeaderboard = ({ isOpen, onClose, streamId }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && streamId) {
      fetchLeaderboard();
    }
  }, [isOpen, streamId]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/streams/${streamId}/leaderboard`);
      setLeaderboard(response.data || []);
    } catch (error) {
      // Generate mock leaderboard
      setLeaderboard([
        { id: '1', name: 'DiamondKing', avatar: null, diamonds: 15000, gifts: 120 },
        { id: '2', name: 'GiftMaster', avatar: null, diamonds: 8500, gifts: 85 },
        { id: '3', name: 'RoyalFan', avatar: null, diamonds: 5200, gifts: 52 },
        { id: '4', name: 'StarGiver', avatar: null, diamonds: 3100, gifts: 31 },
        { id: '5', name: 'HeartLover', avatar: null, diamonds: 1800, gifts: 180 },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getRankStyle = (rank) => {
    switch (rank) {
      case 1: return 'from-[#FFD700] to-[#FFA500]';
      case 2: return 'from-[#C0C0C0] to-[#A0A0A0]';
      case 3: return 'from-[#CD7F32] to-[#8B4513]';
      default: return 'from-gray-700 to-gray-800';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 50 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[#0A0A0F] rounded-3xl border border-white/10 overflow-hidden"
          >
            {/* Header */}
            <div className="p-6 bg-gradient-to-br from-[#FF2A2A]/20 to-[#FFE800]/20 border-b border-white/5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-2xl bg-gradient-to-br from-[#FFD700] to-[#FFA500]">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Gift Leaderboard</h2>
                    <p className="text-sm text-gray-400">Top contributors</p>
                  </div>
                </div>
                <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 text-gray-400">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Leaderboard list */}
            <div className="p-4 max-h-[400px] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-2 border-[#00F0FF]/30 border-t-[#00F0FF] rounded-full animate-spin" />
                </div>
              ) : (
                <div className="space-y-3">
                  {leaderboard.map((user, index) => (
                    <motion.div
                      key={user.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`flex items-center gap-3 p-3 rounded-2xl ${
                        index < 3 ? `bg-gradient-to-r ${getRankStyle(index + 1)} bg-opacity-10` : 'bg-white/5'
                      }`}
                    >
                      {/* Rank */}
                      <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getRankStyle(index + 1)} flex items-center justify-center`}>
                        {index < 3 ? (
                          <Crown className="w-4 h-4 text-white" />
                        ) : (
                          <span className="text-sm font-bold text-white">{index + 1}</span>
                        )}
                      </div>

                      {/* Avatar */}
                      <img
                        src={user.avatar || `https://ui-avatars.com/api/?name=${user.name}&background=0F0F16&color=00F0FF`}
                        alt={user.name}
                        className="w-10 h-10 rounded-full"
                      />

                      {/* Info */}
                      <div className="flex-1">
                        <p className="font-semibold text-white">{user.name}</p>
                        <p className="text-xs text-gray-400">{user.gifts} gifts</p>
                      </div>

                      {/* Diamonds */}
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#00F0FF]/10">
                        <Diamond className="w-4 h-4 text-[#00F0FF]" />
                        <span className="font-bold text-[#00F0FF]">{user.diamonds.toLocaleString()}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Main hook for managing gift state
export const useGiftSystem = (streamId, userId) => {
  const [totalDiamonds, setTotalDiamonds] = useState(0);
  const [floatingGifts, setFloatingGifts] = useState([]);
  const [giftCombo, setGiftCombo] = useState(null);
  const [topGifters, setTopGifters] = useState([]);
  const comboTimeoutRef = useRef(null);
  const comboCountRef = useRef(0);
  const lastGiftRef = useRef(null);

  const addGift = (gift, sender, quantity = 1) => {
    // Add to total diamonds
    setTotalDiamonds(prev => prev + gift.diamonds * quantity);

    // Create floating gift animation
    const giftId = Date.now();
    setFloatingGifts(prev => [...prev, { ...gift, id: giftId, sender }]);

    // Handle combo
    if (lastGiftRef.current?.id === gift.id) {
      comboCountRef.current += quantity;
      setGiftCombo({ gift, count: comboCountRef.current });
      
      // Reset combo timeout
      if (comboTimeoutRef.current) {
        clearTimeout(comboTimeoutRef.current);
      }
      comboTimeoutRef.current = setTimeout(() => {
        setGiftCombo(null);
        comboCountRef.current = 0;
        lastGiftRef.current = null;
      }, 3000);
    } else {
      comboCountRef.current = quantity;
      lastGiftRef.current = gift;
      if (quantity > 1) {
        setGiftCombo({ gift, count: quantity });
      }
    }

    // Update top gifters
    setTopGifters(prev => {
      const existing = prev.find(g => g.id === sender.id);
      if (existing) {
        return prev.map(g => 
          g.id === sender.id 
            ? { ...g, diamonds: g.diamonds + gift.diamonds * quantity }
            : g
        ).sort((a, b) => b.diamonds - a.diamonds);
      }
      return [...prev, { ...sender, diamonds: gift.diamonds * quantity }]
        .sort((a, b) => b.diamonds - a.diamonds)
        .slice(0, 10);
    });
  };

  const removeFloatingGift = (giftId) => {
    setFloatingGifts(prev => prev.filter(g => g.id !== giftId));
  };

  return {
    totalDiamonds,
    floatingGifts,
    giftCombo,
    topGifters,
    addGift,
    removeFloatingGift
  };
};

export { GIFTS, PREMIUM_GIFT_IDS, FloatingGift, GiftCombo };
export default GiftPanel;
